import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { LoadingOutlined } from '@ant-design/icons'
import {
  Checkbox,
  Dropdown,
  Empty,
  Form,
  FormInstance,
  Input,
  notification,
  Typography
} from 'antd'
import clsx from 'clsx'
import dayjs from 'dayjs'
import useGetAnnotationColor from 'hooks/useGetAnnotationColor'
import { useSetRecoilState } from 'recoil'
import Api from 'services/api'
import { hasLinkErrorState } from 'store/buildingStore'
import useSWR from 'swr'
import { AnnotationType, BuildingFile, ListBuildingFiles } from 'types/building'
import Utils from 'utils'
import Validators from 'utils/validators'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import Title from 'components/atoms/Title'
import EllipsisIcon from 'components/icons/EllipsisIcon'

import useFileDuplicateCheck from '../../hooks/useFileDuplicateCheck'
import DuplicateFileModal from '../DuplicateFileModal'
import ModalEditFileName from '../ModalEditFileName'
import ModalFileInfo from '../ModalFileInfo'
import PreviewFile from '../PreviewFile'

type Props = {
  form: FormInstance
  selectedType: AnnotationType | undefined
  selectedTabIndex: number
  selectedTabs: number
  selectedCategory: string
  linkParse: any
  nameParse: any
  currentListFile: BuildingFile[]
  currentListLink: any[]
  setCurrentListFile: Dispatch<SetStateAction<BuildingFile[]>>
  setExistsFile: (files: BuildingFile[]) => void
  setPreviewFileIndex: (index: number | undefined) => void
  isValidatingDetail: boolean
}

export default function BCPSetAnnotationCategoryDetail({
  form,
  selectedType,
  selectedTabIndex,
  selectedTabs,
  selectedCategory,
  linkParse,
  nameParse,
  currentListFile,
  currentListLink = [],
  setCurrentListFile,
  setExistsFile,
  setPreviewFileIndex,
  isValidatingDetail = false
}: Props) {
  const { id } = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const setHasLinkError = useSetRecoilState(hasLinkErrorState)

  const isBuildingMode = useMemo(
    () =>
      !location.pathname.startsWith('/tour') &&
      !location.pathname.startsWith('/bcp'),
    [location.pathname]
  )
  const tabErrorsRef = useRef<Record<number, boolean>>({})

  const { getAnnotationBgColor } = useGetAnnotationColor()

  const annotationBgColor = useMemo(
    () => (selectedType ? getAnnotationBgColor(selectedType) : ''),
    [getAnnotationBgColor, selectedType]
  )

  const [isSelectingFiles, setIsSelectingFiles] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<BuildingFile[]>([])

  const [isUploading, setIsUploading] = useState(false)
  const [selectedFileInfo, setSelectedFileInfo] = useState<BuildingFile | null>(
    null
  )
  const [fileDetailCache, setFileDetailCache] = useState<
    Record<number, BuildingFile>
  >({})
  const [renamingFile, setRenamingFile] = useState<BuildingFile | null>(null)
  const [overwrittenThumbnails, setOverwrittenThumbnails] = useState<
    Record<string, BuildingFile>
  >({})
  const [sortConfig, setSortConfig] = useState<{
    key: 'name' | 'createdAt'
    order: 'asc' | 'desc'
  }>({ key: 'createdAt', order: 'desc' })

  const handleSort = useCallback((key: 'name' | 'createdAt') => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key
      let nextOrder: 'asc' | 'desc'
      if (isSameKey) {
        nextOrder = prev.order === 'asc' ? 'desc' : 'asc'
      } else {
        nextOrder = 'desc'
      }
      return { key, order: nextOrder }
    })
  }, [])
  const containerRef = useRef<HTMLDivElement>(null)
  const [nameWidth, setNameWidth] = useState(450)
  const [sizeWidth, setSizeWidth] = useState(100)
  const resizingTarget = useRef<'name' | 'size' | null>(null)

  const FIXED_BUFFER = 440 // BCP might need a bit more buffer

  const getMaxNameWidth = useCallback(
    (containerWidth: number) => {
      return Math.max(50, containerWidth - FIXED_BUFFER - sizeWidth)
    },
    [sizeWidth]
  )

  const accept = useMemo(() => {
    if (selectedCategory === 'image') return '.jpg,.jpeg,.png,.gif'
    if (selectedCategory === 'video') return '.mp4,.mov'
    if (selectedCategory === 'pdf') return '.pdf'
    return undefined
  }, [selectedCategory])

  useEffect(() => {
    const parent = containerRef.current?.parentElement
    if (!parent) return

    const handleResize = () => {
      const parentWidth = parent.clientWidth
      if (parentWidth > 0) {
        const maxAllowed = getMaxNameWidth(parentWidth)
        setNameWidth((prev) => Math.min(prev, maxAllowed))
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(parent)
    handleResize()
    return () => resizeObserver.disconnect()
  }, [getMaxNameWidth])

  const listRef = useRef<HTMLDivElement>(null)
  const [hasScroll, setHasScroll] = useState(false)
  const [scrollbarWidth, setScrollbarWidth] = useState(0)

  const onMouseMove = (e: MouseEvent) => {
    const target = resizingTarget.current
    if (!target) return

    const parentWidth = containerRef.current?.parentElement?.clientWidth || 510
    const totalBudget = parentWidth - FIXED_BUFFER

    if (target === 'name') {
      setNameWidth((prev) =>
        Math.max(50, Math.min(prev + e.movementX, totalBudget - sizeWidth))
      )
    } else if (target === 'size') {
      setSizeWidth((prev) =>
        Math.max(60, Math.min(prev - e.movementX, totalBudget - nameWidth))
      )
    }
  }

  const onMouseUp = () => {
    resizingTarget.current = null
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }

  const onMouseDown = (target: 'name' | 'size') => (e: React.MouseEvent) => {
    resizingTarget.current = target
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    e.preventDefault()
  }

  const { data, isValidating } = useSWR<ListBuildingFiles>(
    selectedType && selectedTabs
      ? {
          url: 'v1/buildings/files',
          params: {
            buildingId: id,
            category: selectedTabs
          }
        }
      : null
  )

  const { data: allBuildingFiles } = useSWR<ListBuildingFiles>({
    url: 'v1/buildings/files',
    params: {
      buildingId: id,
      category: selectedTabs
    }
  })

  const filteredTabFileList = useMemo(
    () =>
      currentListFile.filter((item) => {
        if (item.id < 0) {
          return item.category === selectedTabs
        }
        return data?.files.files.find((file) => file.id === item.id)
      }),
    [currentListFile, data?.files.files, selectedTabs]
  )

  const filteredSavedFiles = useMemo(() => {
    const list = []
    if (selectedCategory === 'video') {
      list.push(
        ...filteredTabFileList.filter(
          (item) =>
            item.extension === 'mp4' ||
            item.extension === 'mov' ||
            item.extension === 'MOV'
        )
      )
    } else if (selectedCategory === 'image') {
      list.push(
        ...filteredTabFileList.filter(
          (item) =>
            item.extension === 'jpg' ||
            item.extension === 'png' ||
            item.extension === 'jpeg'
        )
      )
    } else if (selectedCategory === 'pdf') {
      list.push(
        ...filteredTabFileList.filter((item) => item.extension === 'pdf')
      )
    } else {
      return []
    }

    return [...list].sort((a, b) => {
      const { key, order } = sortConfig
      let comparison = 0
      if (key === 'name') {
        comparison = a.name.localeCompare(b.name)
      } else if (key === 'createdAt') {
        const dateA = dayjs(a.createdAt)
        const dateB = dayjs(b.createdAt)
        if (dateA.isSame(dateB)) {
          comparison = 0
        } else {
          comparison = dateA.isAfter(dateB) ? 1 : -1
        }
      }
      return order === 'asc' ? comparison : -comparison
    })
  }, [filteredTabFileList, selectedCategory, sortConfig])

  useEffect(() => {
    const checkScroll = () => {
      if (listRef.current) {
        const isScrollable =
          listRef.current.scrollHeight > listRef.current.clientHeight
        setHasScroll(isScrollable)
        setScrollbarWidth(
          listRef.current.offsetWidth - listRef.current.clientWidth
        )
      }
    }

    checkScroll()
    const timeout = setTimeout(checkScroll, 100)

    window.addEventListener('resize', checkScroll)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', checkScroll)
    }
  }, [filteredSavedFiles])

  // Pre-fetch file detail for all images in the list for immediate info display
  useEffect(() => {
    filteredSavedFiles.forEach((f) => {
      if (f.id <= 0) return
      const ext =
        f.extension?.toLowerCase() || f.name.split('.').pop()?.toLowerCase()
      if (!ext || !['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext))
        return
      if (fileDetailCache[f.id]) return
      Api.get<BuildingFile>(`/v1/buildings/files/${f.id}`)
        .then((detail) => {
          if (detail)
            setFileDetailCache((prev) => ({ ...prev, [f.id]: detail }))
        })
        .catch(() => {})
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSavedFiles])

  const filteredSelectableFiles = useMemo(() => {
    if (selectedCategory === 'video') {
      return data?.files.files.filter(
        (item) =>
          item.extension === 'mp4' ||
          item.extension === 'mov' ||
          item.extension === 'MOV'
      )
    }
    if (selectedCategory === 'image') {
      return data?.files.files.filter(
        (item) =>
          item.extension === 'jpg' ||
          item.extension === 'png' ||
          item.extension === 'jpeg'
      )
    }
    if (selectedCategory === 'pdf') {
      return data?.files.files.filter((item) => item.extension === 'pdf')
    }
    return []
  }, [data?.files.files, selectedCategory])

  const handleSelectFile = (file: BuildingFile) => {
    const existingEntry = selectedFiles.find(
      (item) => item.id === file.id || item.name === file.name
    )

    if (existingEntry) {
      setSelectedFiles((prev) =>
        prev.filter((item) => item.id !== existingEntry.id)
      )
      return
    }

    const overwrittenEntry = currentListFile?.find(
      (item) => item.name === file.name
    )

    setSelectedFiles((prev) => [...prev, overwrittenEntry || file])
  }

  const handleClose = () => {
    setIsSelectingFiles(false)
    setSelectedFiles(currentListFile || [])
  }

  const handleSubmit = () => {
    setIsSelectingFiles(false)
    setCurrentListFile(selectedFiles)
  }

  const link = useMemo(
    () =>
      linkParse?.find((item: any, index: any) => index === selectedTabIndex),
    [linkParse, selectedTabIndex]
  )

  const name = useMemo(
    () =>
      nameParse?.find((item: any, index: any) => index === selectedTabIndex),
    [nameParse, selectedTabIndex]
  )

  // ─── processFiles must be defined BEFORE useFileDuplicateCheck ───────────────
  // We use a stable ref so the hook always calls the latest version without
  // triggering a re-render cycle, and without a forward-reference lint error.
  const processFilesRef = useRef<(files: File[], option?: number) => void>(
    () => {}
  )

  const processFiles = (files: File[], option?: number) => {
    setIsUploading(true)
    const category = selectedTabs || 0
    const newFiles: BuildingFile[] = []

    files.forEach((file, index) => {
      let finalName = file.name
      let overwriteID: number | undefined
      let mode: 'overwrite' | 'keep' | undefined

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const match = duplicateFiles.find((d) => d.newFile.name === file.name)
      const effectiveOverwriteID =
        match &&
        (match.oldFile.id > 0 ? match.oldFile.id : match.oldFile.overwriteID)

      if (option === 1) {
        // Replacement mode
        if (effectiveOverwriteID) {
          overwriteID = effectiveOverwriteID
          mode = 'overwrite'
        }
      } else if (option === 2) {
        // Keep both (Numbering)
        const allExistingNames = [
          ...(allBuildingFiles?.files?.files?.map((f) => f.name) || []),
          ...(currentListFile || []).map((f) => f.name),
          ...newFiles.map((f) => f.name)
        ]

        const lastDotIndex = file.name.lastIndexOf('.')
        const extension =
          lastDotIndex !== -1 ? file.name.substring(lastDotIndex) : ''
        const baseName =
          lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name

        let counter = 1
        while (allExistingNames.includes(finalName)) {
          finalName = `${baseName}(${counter})${extension}`
          counter += 1
        }
      }

      const localUrl = URL.createObjectURL(file)
      const newFileObj: BuildingFile = {
        id: -Date.now() - index - Math.floor(Math.random() * 1000000),
        name: finalName,
        s3Path: localUrl,
        mimeType: file.type,
        extension: finalName.split('.').pop()?.toLowerCase() || '',
        createdAt: new Date().toISOString(),
        size: file.size,
        thumbnail: localUrl,
        category,
        pointId: null,
        takenAt: dayjs(file.lastModified).format('YYYY-MM-DDTHH:mm:ssZ'),
        rawFile: file as any,
        overwriteID,
        mode
      }
      newFiles.push(newFileObj)
    })

    setCurrentListFile((prev) => {
      let filteredOldFiles = prev || []
      if (option === 1) {
        // Remove locally added files with the same name if overwriting
        const overwrittenNames = files.map((f) => f.name)
        filteredOldFiles = filteredOldFiles.filter(
          (f) => !overwrittenNames.includes(f.name)
        )
      }
      return [...filteredOldFiles, ...newFiles]
    })

    setSelectedFiles((prev) => {
      const overwritingFiles = newFiles.filter((f) => f.mode === 'overwrite')
      if (overwritingFiles.length === 0) return prev

      const newSelected = [...prev]
      overwritingFiles.forEach((newFile) => {
        setOverwrittenThumbnails((thumbs) => ({
          ...thumbs,
          [newFile.name]: newFile
        }))
        const existingIdx = newSelected.findIndex(
          (f) => f.name === newFile.name
        )
        if (existingIdx !== -1) {
          // Update the existing selection with the new file info (specifically for thumbnail/blob)
          newSelected[existingIdx] = {
            ...newSelected[existingIdx],
            ...newFile,
            id: newSelected[existingIdx].id // Keep the original ID to maintain selection if needed
          }
        } else {
          newSelected.push(newFile)
        }
      })
      return newSelected
    })

    setIsUploading(false)
  }

  // Keep the ref in sync with the latest processFiles closure on every render
  useEffect(() => {
    processFilesRef.current = processFiles
  })

  const {
    duplicateFiles,
    showDuplicateModal,
    selectedDuplicateOption,
    setSelectedDuplicateOption,
    fileInputRef: sharedFileInputRef,
    handleFileUpload,
    handleConfirmDuplicate,
    handleCancelDuplicate,
    setDuplicateFiles
  } = useFileDuplicateCheck({
    currentListFile: currentListFile || [],
    allBuildingFiles,
    processFiles: (files: File[], option?: number) =>
      processFilesRef.current(files, option),
    allowedExtensions: [
      ...(selectedCategory === 'image' ? ['jpg', 'jpeg', 'png', 'gif'] : []),
      ...(selectedCategory === 'video' ? ['mp4', 'mov'] : []),
      ...(selectedCategory === 'pdf' ? ['pdf'] : [])
    ]
  })
  // ─────────────────────────────────────────────────────────────────────────────

  const handleRemoveFileFromAnnotation = (item: BuildingFile) => {
    setCurrentListFile((prev) => prev.filter((f) => f.id !== item.id))
  }

  const handleDownloadFile = async (item: BuildingFile) => {
    try {
      notification.info({
        message: t('building.downloading'),
        key: 'downloading',
        icon: <LoadingOutlined className="theme-text-primary" />
      })
      // Fetch fresh file detail from API to avoid expired S3 URLs if it's not a local file
      let downloadPath = item.s3Path
      if (item.id > 0) {
        const fileDetail = await Api.get<BuildingFile>(
          `/v1/buildings/files/${item.id}`
        )
        downloadPath = fileDetail?.s3Path || item.s3Path
      }

      const response = await fetch(downloadPath)
      if (!response.ok) throw new Error('Failed to fetch file content')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = Utils.getDownloadFileName(item)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      notification.success({
        message: t('building.downloadSuccess'),
        key: 'downloading'
      })
    } catch (error) {
      console.error('Download error:', error)
      notification.error({
        message: t('common.error'),
        key: 'downloading'
      })
    }
  }

  const handleMenuClick = (key: string, item: BuildingFile) => {
    switch (key) {
      case 'open': {
        const index = [...filteredSavedFiles]
          .sort((a, b) => a.name.localeCompare(b.name))
          .findIndex((f) => String(f.id) === String(item.id))
        setPreviewFileIndex(index)
        break
      }
      case 'rename':
        setRenamingFile(item)
        break
      case 'download':
        handleDownloadFile(item)
        break
      case 'info': {
        const detail = fileDetailCache[item.id]
        setSelectedFileInfo(detail || item)
        break
      }
      case 'delete':
        handleRemoveFileFromAnnotation(item)
        break
      default:
        break
    }
  }

  const formatBytes = (
    bytes: number | string | undefined | null,
    decimals = 1
  ) => {
    if (!bytes) return '--'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(Number(bytes)) / Math.log(k))
    return `${parseFloat((Number(bytes) / k ** i).toFixed(dm))}${sizes[i]}`
  }

  const formatFileInfo = (item: BuildingFile) => {
    const { extension, name } = item
    const ext = extension?.toLowerCase() || name.split('.').pop()?.toLowerCase()
    const { extraInfo } = item

    if (ext === 'pdf') {
      const pageCount = extraInfo?.page_count
      return pageCount ? `${pageCount}ページ` : '--'
    }

    const typesVideo = ['mov', 'mp4']
    if (ext && typesVideo.includes(ext)) {
      const duration = extraInfo?.duration
      if (duration) {
        const mins = Math.floor(duration / 60)
        const secs = Math.floor(duration % 60)
        return `${mins}:${secs.toString().padStart(2, '0')}`
      }
      return '--'
    }

    return formatBytes(extraInfo?.size)
  }

  useEffect(() => {
    if (currentListFile) {
      setSelectedFiles(currentListFile)
    }
  }, [currentListFile])

  useEffect(() => {
    setExistsFile(
      [...filteredSavedFiles].sort((a, b) => a.name.localeCompare(b.name))
    )
  }, [filteredSavedFiles, setExistsFile])

  const linksStage = Form.useWatch(['linksStage', selectedTabs], form)

  const scrollRef = useRef<HTMLDivElement>(null)
  const prevLinksLength = useRef(0)
  const initializedTabs = useRef<Set<number>>(new Set())

  useEffect(() => {
    if (selectedCategory === 'link' && selectedTabs && !isValidatingDetail) {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0
      }
      prevLinksLength.current = 0

      const isAlreadyInitialized = initializedTabs.current.has(selectedTabs)
      const currentStageLinks = form.getFieldValue(['linksStage', selectedTabs])

      if (
        !isAlreadyInitialized ||
        currentStageLinks === undefined ||
        (Array.isArray(currentStageLinks) && currentStageLinks.length === 0)
      ) {
        const initialLinks =
          currentListLink?.filter((l: any) => l.category === selectedTabs) || []
        if (initialLinks.length > 0) {
          form.setFieldValue(['linksStage', selectedTabs], initialLinks)
        } else if (!currentStageLinks || currentStageLinks.length === 0) {
          form.setFieldValue(
            ['linksStage', selectedTabs],
            [{ link: link || '', name: name || '' }]
          )
        }
        initializedTabs.current.add(selectedTabs)
      }
    }
  }, [
    selectedCategory,
    selectedTabs,
    form,
    currentListLink,
    link,
    name,
    isValidatingDetail
  ])

  useEffect(() => {
    if (selectedCategory === 'link' && linksStage) {
      const hasErrorInCurrentTab = form
        .getFieldsError()
        .some(
          (f) =>
            Array.isArray(f.name) &&
            f.name[0] === 'linksStage' &&
            f.name[1] === selectedTabs &&
            f.errors.length > 0
        )

      tabErrorsRef.current[selectedTabs] = hasErrorInCurrentTab

      const hasAnyError = Object.values(tabErrorsRef.current).some(Boolean)
      setHasLinkError(hasAnyError)
    }

    if (
      linksStage &&
      prevLinksLength.current > 0 &&
      linksStage.length > prevLinksLength.current
    ) {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }
    }
    prevLinksLength.current = linksStage?.length || 0
  }, [
    linksStage,
    selectedCategory,
    selectedTabs,
    form,
    setHasLinkError,
    tabErrorsRef
  ])

  if (selectedCategory === 'link') {
    return (
      <Form.List name={['linksStage', selectedTabs]}>
        {(fields, { add, remove }) => (
          <>
            <div
              ref={scrollRef}
              className="h-[300px] pt-9 pl-[31px] pr-[50px] overflow-y-auto overflow-x-hidden custom-scrollbar"
            >
              {fields.map(({ key, name, ...restField }) => (
                <div
                  key={key}
                  className="relative mb-8 pb-4 border-0 border-b border-[#EEEFF2] last:border-b-0 last:mb-0"
                >
                  <div className="flex flex-col">
                    <Form.Item
                      {...restField}
                      name={[name, 'link']}
                      label={t('building.inputURLAnnotation')}
                      rules={[
                        { validator: Validators.linkAnnotationValidator(t) }
                      ]}
                    >
                      <Input
                        placeholder="https://"
                        className="h-[38px] rounded-[6px] py-[9px] px-[16px] text-[14px] w-full"
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      label={t('building.inputNameAnnotation')}
                      className="!mb-1"
                      rules={[
                        {
                          validator:
                            Validators.keywordsAndLengthCustomValidator(
                              t,
                              500,
                              undefined,
                              true
                            )
                        }
                      ]}
                    >
                      <Input
                        placeholder={t('building.placeholder')}
                        className="h-[38px] rounded-[6px] py-[9px] px-[16px] text-[14px] w-full"
                      />
                    </Form.Item>
                    {name > 0 && (
                      <div className="flex justify-end mt-0">
                        <Button
                          sizing="w-[84px] h-[32px] rounded-[4px]"
                          className="!bg-[#B80808] !text-white border-0 hover:opacity-80 text-[13px] font-bold"
                          onClick={() => remove(name)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-4">
              <Button
                sizing="w-[160px] h-[40px]"
                onClick={() => add({ link: '', name: '' })}
                style={{
                  backgroundColor: '#657cff',
                  borderColor: '#657cff',
                  color: '#ffffff'
                }}
              >
                {t('translation.add')}
              </Button>
            </div>
          </>
        )}
      </Form.List>
    )
  }

  return (
    <>
      <div className="flex flex-col grow min-w-0" ref={containerRef}>
        <Spinner spinning={isValidating}>
          <div
            className="grid items-center px-5 py-2 border-0 border-b border-solid border-[#EEEFF2] group"
            style={{
              gridTemplateColumns: `${nameWidth}px 4px 1fr 4px ${sizeWidth}px 80px`,
              gap: '12px',
              paddingRight: hasScroll ? `${20 + scrollbarWidth}px` : '20px'
            }}
          >
            <div
              className="flex items-center gap-1 cursor-pointer select-none"
              onClick={() => handleSort('name')}
            >
              <Typography
                className={clsx('font-bold text-[14px] whitespace-nowrap', {
                  'text-[#021120]': sortConfig.key === 'name',
                  'text-[#A0A0A0]': sortConfig.key !== 'name'
                })}
              >
                名前
                {sortConfig.key === 'name' &&
                  (sortConfig.order === 'asc' ? '↑' : '↓')}
              </Typography>
            </div>
            <div
              onMouseDown={onMouseDown('name')}
              className="h-full w-[4px] cursor-col-resize bg-transparent border-0 border-l border-r border-solid border-transparent group-hover:border-[#A0A0A0] transition-colors"
            />
            <div
              className="flex items-center gap-1 cursor-pointer select-none"
              onClick={() => handleSort('createdAt')}
            >
              <Typography
                className={clsx('font-bold text-[14px] whitespace-nowrap', {
                  'text-[#021120]': sortConfig.key === 'createdAt',
                  'text-[#A0A0A0]': sortConfig.key !== 'createdAt'
                })}
              >
                更新日
                {sortConfig.key === 'createdAt' &&
                  (sortConfig.order === 'asc' ? '↑' : '↓')}
              </Typography>
            </div>
            <div
              onMouseDown={onMouseDown('size')}
              className="h-full w-[4px] cursor-col-resize bg-transparent border-0 border-l border-r border-solid border-transparent group-hover:border-[#A0A0A0] transition-colors"
            />
            <Typography className="text-[#021120] font-bold text-[14px] text-left">
              サイズ
            </Typography>
            <div />
          </div>
          <div
            ref={listRef}
            className="h-[300px] overflow-auto overflow-x-hidden"
          >
            {!isValidating && filteredSavedFiles?.length ? (
              filteredSavedFiles.map((item) => (
                <div
                  key={item.id}
                  className="grid items-center gap-3 px-5 min-h-[60px] cursor-pointer border-0 border-b border-solid border-[#EEEFF2] hover:bg-[#F3F6F6]"
                  style={{
                    gridTemplateColumns: `${nameWidth}px 4px 1fr 4px ${sizeWidth}px 80px`,
                    gap: '12px'
                  }}
                >
                  <div className="flex items-center gap-3 overflow-hidden min-w-0">
                    <div className="w-[60px] h-[40px] shrink-0 flex items-center justify-center">
                      <PreviewFile file={item} size="small" />
                    </div>
                    <Typography.Text
                      className="text-[#021120] text-[13px] leading-[20px] font-medium"
                      ellipsis={{
                        tooltip: item.name
                      }}
                    >
                      {item.name}
                    </Typography.Text>
                  </div>
                  <div />
                  <Typography className="text-[#021120] text-[13px] font-medium whitespace-nowrap">
                    {dayjs(item.createdAt).format('YYYY/MM/DD')}
                  </Typography>
                  <div />
                  <Typography className="text-[#021120] text-[13px] font-medium whitespace-nowrap text-left">
                    {formatFileInfo(item)}
                  </Typography>
                  <Dropdown
                    menu={{
                      items: [
                        { key: 'open', label: '開く', disabled: item.id < 0 },
                        { key: 'rename', label: '名前を変更' },
                        {
                          key: 'download',
                          label: 'ダウンロード',
                          disabled: item.id < 0
                        },
                        {
                          key: 'info',
                          label: 'ファイル情報',
                          disabled: item.id < 0
                        },
                        { key: 'delete', label: '削除' }
                      ],
                      onClick: ({ key }) => handleMenuClick(key, item),
                      style: { backgroundColor: 'white' }
                    }}
                    trigger={['click']}
                    overlayClassName="annotation-folder-dropdown"
                  >
                    <div
                      className="flex justify-center items-center w-6 h-6 rounded hover:bg-[#3263B71A] transition-colors cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EllipsisIcon />
                    </div>
                  </Dropdown>
                </div>
              ))
            ) : (
              <Empty className="mt-5" />
            )}
          </div>
        </Spinner>

        <div className="flex gap-10 justify-center mt-14">
          <input
            type="file"
            ref={sharedFileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            accept={accept}
            multiple
          />
          <Button
            sizing="w-[240px] h-[60px]"
            className={clsx(
              annotationBgColor,
              'text-white border-0 hover:!text-white hover:opacity-80'
            )}
            onClick={() => setIsSelectingFiles(true)}
          >
            {t('building.selectFile')}
          </Button>
          <Button
            sizing="w-[240px] h-[60px]"
            className={clsx(
              annotationBgColor,
              'text-white border-0 hover:!text-white hover:opacity-80'
            )}
            onClick={() => sharedFileInputRef.current?.click()}
            loading={isUploading}
          >
            {t('common.upload')}
          </Button>
        </div>
      </div>

      {selectedFileInfo && (
        <ModalFileInfo
          file={selectedFileInfo}
          onCancel={() => setSelectedFileInfo(null)}
        />
      )}
      {renamingFile && (
        <ModalEditFileName
          file={renamingFile}
          existingNames={[
            // files already saved on server
            ...(allBuildingFiles?.files.files
              .filter((f) => f.id !== renamingFile.id)
              .map((f) => f.name) || []),
            // all local files in the current selection
            ...(currentListFile || [])
              .filter((f) => f.id !== renamingFile.id)
              .map((f) => f.name)
          ]}
          onCancel={() => setRenamingFile(null)}
          onSuccess={(newName) => {
            setCurrentListFile((prev) =>
              prev.map((f) =>
                String(f.id) === String(renamingFile.id)
                  ? {
                      ...f,
                      name: newName,
                      originalName: f.originalName || f.name
                    }
                  : f
              )
            )
            setSelectedFiles((prev) =>
              prev.map((f) =>
                String(f.id) === String(renamingFile.id)
                  ? {
                      ...f,
                      name: newName,
                      originalName: f.originalName || f.name
                    }
                  : f
              )
            )
            setRenamingFile(null)
          }}
        />
      )}

      {isSelectingFiles && (
        <Modal
          open={isSelectingFiles}
          width={900}
          className="paddingless-modal"
          onCancel={handleClose}
        >
          <div className="pt-10 pb-5">
            <Title title={t(`translation.${selectedTabIndex + 1}`)} />

            <Spinner spinning={isValidating}>
              <div className="mx-7 my-[30px] h-[360px] overflow-y-auto">
                {Boolean(!isValidating && filteredSelectableFiles?.length) &&
                  filteredSelectableFiles
                    ?.sort((a, b) => a.name.localeCompare(b.name))
                    ?.map((item, index) => (
                      <div
                        key={item.id}
                        className={clsx(
                          'h-[60px] grid grid-cols-[auto_auto_1fr] gap-5 items-center cursor-pointer',
                          'border-0 border-b border-solid border-[#EEEFF2]',
                          { 'border-t': index === 0 }
                        )}
                        onClick={() => handleSelectFile(item)}
                      >
                        <Checkbox
                          checked={Boolean(
                            selectedFiles.find(
                              (file) =>
                                file.id === item.id ||
                                (item.id > 0 && file.name === item.name)
                            )
                          )}
                        />
                        <PreviewFile
                          file={
                            selectedFiles.find(
                              (f) => f.id === item.id || f.name === item.name
                            ) ||
                            overwrittenThumbnails[item.name] ||
                            item
                          }
                          size="large"
                        />
                        <Typography.Text
                          className="text-base font-medium text-[#021120]"
                          ellipsis={{
                            tooltip: item.name
                          }}
                        >
                          {item.name}
                        </Typography.Text>
                      </div>
                    ))}

                {Boolean(!isValidating && !filteredSelectableFiles?.length) && (
                  <Empty className="mt-5" />
                )}
              </div>
            </Spinner>

            <div className="pt-5 flex gap-10 justify-center border-0 border-t border-solid border-[#EEEFF2]">
              <Button sizing="w-[140px] h-[50px]" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                type="primary"
                sizing="w-[140px] h-[50px]"
                onClick={handleSubmit}
                disabled={selectedFiles.length === 0}
                style={{
                  color:
                    selectedFiles.length === 0 && isBuildingMode
                      ? '#A0A0A0'
                      : undefined
                }}
              >
                {t('common.confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <DuplicateFileModal
        open={showDuplicateModal}
        duplicateFiles={duplicateFiles}
        selectedOption={selectedDuplicateOption}
        setSelectedOption={setSelectedDuplicateOption}
        onCancel={handleCancelDuplicate}
        onConfirm={handleConfirmDuplicate}
        afterClose={() => setDuplicateFiles([])}
      />
    </>
  )
}
